import Button from '@lyra/core/components/Button'
import { PlusSquare, X } from '@lyra/core/components/Icon'
import Section, { SectionProps } from '@lyra/core/components/Section'
import { Ticker } from '@lyra/web/constants/instruments'
import { OrderQuoteParams, SetOrderQuoteParams } from '@lyra/web/constants/order'
import { LegsUnpriced, SetRfqParams } from '@lyra/web/constants/rfqs'
import useOrderOrRfqQueryParams from '@lyra/web/hooks/useOrderOrRfqQueryParams'
import useOrderParamsStateForInstrument from '@lyra/web/hooks/useOrderParamsStateForInstrument'
import useSelectedInstruments from '@lyra/web/hooks/useSelectedInstruments'
import useStrategy from '@lyra/web/hooks/useStrategy'
import useTickers from '@lyra/web/hooks/useTickers'
import { formatInstrumentWithDirection } from '@lyra/web/utils/instruments'
import { formatStrategyName } from '@lyra/web/utils/strategies'
import { useMemo } from 'react'

import RfqTradeForm from './RfqTradeForm'
import TradeForm from './TradeForm'
import TradeFormOrderTypeTabs from './TradeFormOrderTypeTabs'

const EMPTY_SECTION_HEIGHT = 360

type Props = {
  onSubmitOrder?: () => void
} & SectionProps

const WrappedOrderbookTradeForm = ({
  ticker,
  orderParams: orderParamsNullable,
  setOrderParams: setOrderParamsNullable,
  setRfqParams,
  onSubmitOrder,
}: {
  ticker: Ticker
  orderParams: OrderQuoteParams | null
  setOrderParams: SetOrderQuoteParams
  setRfqParams: SetRfqParams
  onSubmitOrder?: () => void
}) => {
  const [orderParams, setOrderParams] = useOrderParamsStateForInstrument(
    ticker.instrument_name,
    orderParamsNullable,
    setOrderParamsNullable
  )

  return (
    <TradeForm
      ticker={ticker}
      orderParams={orderParams}
      setOrderParams={setOrderParams}
      setRfqParams={setRfqParams}
      onSubmitOrder={onSubmitOrder}
    />
  )
}

export default function TradeFormQueryParams({ onSubmitOrder, ...sectionProps }: Props) {
  const { instruments, rfqParams, setRfqParams, orderParams, setOrderParams, isRfq } =
    useOrderOrRfqQueryParams()

  const { removeInstrument, removeAllInstruments } = useSelectedInstruments()

  const legs: LegsUnpriced = useMemo(
    () =>
      isRfq
        ? rfqParams.legs
        : orderParams
        ? [
            {
              amount: orderParams.amount,
              instrument_name: orderParams.instrument_name,
              direction: orderParams.direction,
            },
          ]
        : [],
    [isRfq, rfqParams, orderParams]
  )

  const tickers = useTickers(legs.map((leg) => leg.instrument_name))

  const strategy = useStrategy(tickers, legs)

  if (!legs.length) {
    return (
      <Section height={EMPTY_SECTION_HEIGHT} {...sectionProps}>
        <Section.Subheader title="Select an instrument" />
        <Section.YStack flexGrow={1} justifyContent="center" alignItems="center">
          <PlusSquare width={64} height={64} strokeWidth={0.5} color="secondary" />
        </Section.YStack>
      </Section>
    )
  }

  if (!tickers) {
    return (
      <Section height={EMPTY_SECTION_HEIGHT} {...sectionProps}>
        <Section.Spinner flexGrow={1} flexShrink={1} />
      </Section>
    )
  }

  const strategyName =
    legs.length === 1
      ? formatInstrumentWithDirection(legs[0].instrument_name, legs[0].direction === 'buy', {
          showExpiry: true,
        })
      : formatStrategyName(strategy)

  return (
    <Section {...sectionProps}>
      <Section.Subheader
        title={strategyName}
        rightContent={
          <Button icon={<X />} size="sm" isTransparent onPress={removeAllInstruments} />
        }
      />
      <TradeFormOrderTypeTabs
        isRfq={isRfq}
        instruments={instruments}
        orderParams={orderParams}
        setOrderParams={setOrderParams}
        rfqParams={rfqParams}
        setRfqParams={setRfqParams}
      />
      {isRfq ? (
        <RfqTradeForm
          rfqParams={rfqParams}
          setRfqParams={setRfqParams}
          onRemoveInstrument={removeInstrument}
          onSubmitOrder={onSubmitOrder}
          showAddLegsPrompt
        />
      ) : (
        <WrappedOrderbookTradeForm
          ticker={tickers[Object.keys(tickers)[0]]!} // always defined when !isRfq and tickers exist
          orderParams={orderParams}
          setOrderParams={setOrderParams}
          setRfqParams={setRfqParams}
          onSubmitOrder={onSubmitOrder}
        />
      )}
    </Section>
  )
}
